import React, { ReactNode, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { TabsWrapper, TabWrapper, TabContent, TabInner, TabNav, TabNavList, TabNavLink, TabFooter } from "./styled";

export const Tabs = ({ children, vertical, links, footer, stickyFooter, fillHeight }: { 
  children: ReactNode; 
  vertical?: boolean; 
  links?: { key: string; label: string }[]; 
  footer?: ReactNode; 
  stickyFooter?: boolean;
  fillHeight?: boolean;
}) => {
  
  const location = useLocation();
  const navigate = useNavigate();

  // Get the tab from the URL, default to the first tab if none is found
  const searchParams = new URLSearchParams(location.search);
  const tabFromUrl = searchParams.get("tab") || links?.[0]?.key || "";

  // State for active tab (derived from URL)
  const [activeTabKey, setActiveTabKey] = React.useState(tabFromUrl);

  // Sync tab changes to the URL
  useEffect(() => {
    if (activeTabKey) {
      const params = new URLSearchParams(location.search);
      params.set("tab", activeTabKey);
      navigate(`?${params.toString()}`, { replace: true });
    }
  }, [activeTabKey, navigate, location.search]);

  const handleTabClick = (key: string) => {
    setActiveTabKey(key);
  };

  /**
   * Session 36 fix: when the active tab changes, `children.find(...)` returns
   * a different <Tab> element but React reconciles it to the *same* fiber
   * because Tab is the same component type at the same position — with no
   * key differentiation. That reuse silently preserves child component state
   * (e.g. TableData's `useRef(api)` initial value), which meant switching
   * from RFQs → Invoices left TableData calling the previous tab's API.
   *
   * Forcing a keyed Fragment tied to `activeTabKey` makes React see a new
   * subtree on every switch and unmount+remount the child cleanly. This is
   * the correct behavior for tab panels — no consumer relies on state
   * persisting across a tab switch.
   */
  const activeChild = React.useMemo(() => {
    if (!Array.isArray(children)) return children;
    return children.find((child: any) => child?.props?.itemKey === activeTabKey);
  }, [children, activeTabKey]);

  return (
    <TabsWrapper $fillHeight={fillHeight}>
      <TabWrapper $vertical={vertical} $fillHeight={fillHeight}>
        {(links || []).length > 0 && (
          <TabNav>
            <TabNavList $vertical={vertical}>
              {(links || []).map((link, index) => (
                <TabNavLink 
                  key={index} 
                  onClick={() => handleTabClick(link.key)}
                  $active={activeTabKey === link.key}
                  $vertical={vertical}
                >
                  {link.label}
                </TabNavLink>
              ))}
            </TabNavList>
          </TabNav>
        )}
        <TabContent $fillHeight={fillHeight}>
          {Array.isArray(children) ? (
            <React.Fragment key={activeTabKey}>{activeChild}</React.Fragment>
          ) : (
            activeChild
          )}
        </TabContent>
      </TabWrapper>
      {footer && <TabFooter $sticky={stickyFooter}>{footer}</TabFooter>}
    </TabsWrapper>
  );
};

export const Tab = ({ children, itemKey }: { children: any; itemKey: string }) => {
  return <TabInner>{children}</TabInner>;
};
